/**
 * UserMenu — Person icon with dropdown for profile links and logout.
 *
 * Follows the same pattern as WalletHeaderButton:
 *   - Icon button with border
 *   - Dropdown with outside-click-to-close via useRef
 *   - Navigation via useNavigate
 *
 * Props:
 *   - user: the current user object (from AuthContext)
 *   - onLogout: logout handler
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/UserMenu.css';

export default function UserMenu({ user, onLogout }) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [dropdownOpen]);

  const handleNavigate = (path) => {
    setDropdownOpen(false);
    navigate(path);
  };

  const handleLogout = () => {
    setDropdownOpen(false);
    onLogout();
  };

  return (
    <div className="user-menu" ref={dropdownRef}>
      <button
        className="user-menu-btn"
        onClick={() => setDropdownOpen(!dropdownOpen)}
        aria-label="User menu"
      >
        <svg className="user-menu-icon" viewBox="0 0 20 20" fill="currentColor" width="18" height="18">
          <path d="M10 10a4 4 0 100-8 4 4 0 000 8zm-7 8a7 7 0 1114 0H3z" />
        </svg>
      </button>

      {dropdownOpen && (
        <div className="user-menu-dropdown">
          <div className="user-menu-header">
            <span className="user-menu-name">{user.name || user.email}</span>
            {user.name && <span className="user-menu-email">{user.email}</span>}
          </div>

          <div className="user-menu-links">
            <button className="user-menu-item" onClick={() => handleNavigate('/profile')}>
              Profile
            </button>
            <button className="user-menu-item" onClick={() => handleNavigate('/profile?tab=payments')}>
              Payments
            </button>
            <button className="user-menu-item" onClick={() => handleNavigate('/profile?tab=spending')}>
              Spending
            </button>
            <button className="user-menu-item" onClick={() => handleNavigate('/profile?tab=wallet')}>
              Payment Methods
            </button>
            <button className="user-menu-item" onClick={() => handleNavigate('/profile?tab=security')}>
              Security
            </button>
          </div>

          <div className="user-menu-footer">
            <button className="user-menu-item danger" onClick={handleLogout}>
              Log Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
